import CApiHelper from '@frontrow/console/src/entities/helpers/common-api-helper'
import {
  type LicensePostT,
  type LicensesResponse
} from '@frontrow/console/src/entities/models/license'
import {ajaxJsonCall, ajaxCall} from '@freckle/ajax'
import {type operations} from '@frontrow/console/src/open-api/fancy-api/generated-client'

import {
  consoleFancyClient,
  extractData,
  type fancyOperations,
  type FancyOk
} from '@frontrow/console/src/open-api/clients'

import {type BasicSchoolWithRenaissanceClientT} from '@frontrow/console/src/models/school'
import {type FetchDataParamsT} from '@frontrow/console/src/components/common/data-table'
import {createDatatableUrl} from '@frontrow/console/src/helpers/data-table'

// Not migrated to consoleFancyClient: createDatatableUrl builds an arbitrary
// query string shared by every domain's datatable, and mapping its filters
// onto openapi-fetch's typed `params.query` deserves its own pass across all
// of those domains rather than a one-off cast here.
export function fetchSchools(
  fetchDataParams?: FetchDataParamsT | null
): Promise<operations['getSchoolsR']['responses'][200]['content']['application/json']> {
  const baseUrl = CApiHelper.fancyPaths.v2.schools._()
  const url = createDatatableUrl(baseUrl, fetchDataParams)
  return ajaxJsonCall({url, method: 'GET'})
}

// Not migrated to consoleFancyClient: BasicSchoolWithRenaissanceClientT sends
// three things `SchoolPATCH`
// (backend/fancy-api/library/Freckle/Api/Handlers/School.hs) has no field for
// — `classLinkSchoolId`, `renaissanceSchoolClient.sharedRosteringEnabled`, and
// a `Date` where the wire wants a string. A typed body has to resolve all
// three, and the first one is load-bearing for bulk edit, so it gets its own
// PR rather than riding along with the straight swaps.
export function updateSchool(
  schoolId: number,
  updatedSchool: BasicSchoolWithRenaissanceClientT
): Promise<operations['patchSchoolR']['responses'][200]['content']['application/json']> {
  return ajaxJsonCall({
    url: `${CApiHelper.fancyPaths.v2.schools.school._(schoolId)}`,
    method: 'PATCH',
    data: JSON.stringify(updatedSchool)
  })
}

// The handler returns individual licenses to console agents, which console
// always is (backend/fancy-api/library/Freckle/Api/Handlers/School/Licenses.hs:62-68).
export async function fetchSchoolLicenses(schoolId: number): Promise<LicensesResponse> {
  const licenses = extractData(
    await consoleFancyClient.GET('/2/schools/{SchoolP-SchoolId}/licenses', {
      params: {path: {'SchoolP-SchoolId': schoolId}}
    })
  )
  return licenses as LicensesResponse
}

// `startsAt`/`expiresAt` are `Date` on `LicensePostT` and date-time strings on
// the wire. The old `JSON.stringify(newLicense)` call converted them via
// `Date.prototype.toJSON`; openapi-fetch's `body` wants the wire shape, so the
// conversion is explicit at the call site instead.
export async function postSchoolLicense(
  schoolId: number,
  newLicense: LicensePostT
): Promise<FancyOk<fancyOperations['postSchoolLicensesR']>> {
  return extractData(
    await consoleFancyClient.POST('/2/schools/{SchoolP-SchoolId}/licenses', {
      params: {path: {'SchoolP-SchoolId': schoolId}},
      body: {
        subject: newLicense.subject,
        startsAt: newLicense.startsAt.toISOString(),
        expiresAt: newLicense.expiresAt.toISOString(),
        count: newLicense.count
      }
    })
  )
}

export async function postSchoolProvision(clientId: string): Promise<unknown> {
  const {data} = await consoleFancyClient.POST('/2/schools/provision', {
    body: {clientId}
  })
  return data
}

export async function deleteSchoolLicense(licenseId: number): Promise<unknown> {
  const {data} = await consoleFancyClient.DELETE('/2/licenses/{LicenseP-LicenseId}', {
    params: {path: {'LicenseP-LicenseId': licenseId}}
  })
  return data
}

export async function fetchSchool(
  schoolId: number
): Promise<FancyOk<fancyOperations['getSchoolR']>> {
  return extractData(
    await consoleFancyClient.GET('/2/schools/{SchoolP-SchoolId}', {
      params: {path: {'SchoolP-SchoolId': schoolId}}
    })
  )
}

export async function fetchSchoolsByDistrict(
  districtId: number
): Promise<FancyOk<fancyOperations['getDistrictSchoolsR']>> {
  return extractData(
    await consoleFancyClient.GET('/2/districts/{DistrictP-DistrictId}/schools', {
      params: {path: {'DistrictP-DistrictId': districtId}}
    })
  )
}

export function deleteIdentityProviderAssociations(schoolId: number): Promise<string> {
  return ajaxCall({
    url: CApiHelper.fancyPaths.v2.schools.school.school_identity_providers.associations(schoolId),
    method: 'DELETE',
    contentType: 'application/json; charset=utf-8',
    dataType: 'text'
  })
}

export async function deleteSchool(
  schoolId: number
): Promise<FancyOk<fancyOperations['deleteSchoolR']>> {
  return extractData(
    await consoleFancyClient.DELETE('/2/schools/{SchoolP-SchoolId}', {
      params: {path: {'SchoolP-SchoolId': schoolId}}
    })
  )
}

export async function fetchSchoolDeletionInfo(
  schoolId: number
): Promise<FancyOk<fancyOperations['getSchoolDeletionInfoR']>> {
  return extractData(
    await consoleFancyClient.GET('/2/schools/{SchoolP-SchoolId}/deletion-info', {
      params: {path: {'SchoolP-SchoolId': schoolId}}
    })
  )
}

export async function postSchoolMerge(
  sourceSchoolId: number,
  targetSchoolId: number
): Promise<FancyOk<fancyOperations['postSchoolMergeR']>> {
  // Note: a school is merged _into_ a target school. The source is specified in the JSON body
  return extractData(
    await consoleFancyClient.POST('/2/schools/{SchoolP-SchoolId}/merge', {
      params: {path: {'SchoolP-SchoolId': targetSchoolId}},
      body: {sourceSchoolId}
    })
  )
}

export async function postSchoolIdpSync(schoolId: number): Promise<void> {
  await consoleFancyClient.POST('/2/schools/{SchoolP-SchoolId}/identity-providers/sync', {
    params: {path: {'SchoolP-SchoolId': schoolId}}
  })
}
